/**
 * `/admin/audit` view — system-admin audit log.
 *
 * A read-only, most-recent-first table of every admin mutation across the
 * LLM, signup, and user-management surfaces (`admin_audit_log`). Imperative
 * onMount fetch + populate, same pattern as the other admin pages; 401/404
 * → the shared not-found / sign-in overlay.
 */
import { pageHeader } from '@dl/ui';
import {
  div,
  main,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';

import type { AdminResult } from './admin-fetch.js';
import { maybeRenderOverlay as renderOverlay } from './admin-overlay.js';
import { listAudit, type AuditEntry } from './audit-api.js';
import { onAdminMount } from './mount.js';

function formatAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function el(
  tag: string,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElement {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const ch of children) {
    node.appendChild(typeof ch === 'string' ? document.createTextNode(ch) : ch);
  }
  return node;
}

function maybeRenderOverlay(root: Element, result: AdminResult<unknown>): boolean {
  return renderOverlay(root, result, {
    testid: 'admin-audit-overlay',
    notFoundHeading: t('admin.audit.not_found.heading'),
    notFoundBody: t('admin.audit.not_found.body'),
    unauthHeading: t('admin.audit.unauthenticated.heading'),
    unauthBody: t('admin.audit.unauthenticated.body'),
    unauthLink: t('admin.audit.unauthenticated.link'),
    signInTestid: 'admin-audit-sign-in-link',
    hideSelector: '.admin-audit-table-wrap',
    introSelector: '.dl-page-header',
  });
}

/** Pull the most useful identifier out of a metadata blob for the table. */
function summariseTarget(e: AuditEntry): string {
  const m = e.metadata;
  if (m && typeof m === 'object') {
    const email = m['target_email'];
    if (typeof email === 'string') return email;
  }
  return e.provider_id ?? e.key_id ?? '—';
}

function renderTable(tableEl: Element, entries: AuditEntry[]): void {
  while (tableEl.firstChild) tableEl.removeChild(tableEl.firstChild);
  if (entries.length === 0) {
    tableEl.appendChild(el('p', { class: 'admin-panel-status' }, [t('admin.audit.empty')]));
    return;
  }
  const table = el('table', { class: 'admin-audit-table', 'data-testid': 'admin-audit-table' });
  const thead = el('thead');
  const headRow = el('tr');
  for (const key of ['when', 'action', 'admin', 'target', 'details'] as const) {
    headRow.appendChild(el('th', { scope: 'col' }, [t(`admin.audit.col.${key}`)]));
  }
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = el('tbody');
  for (const e of entries) {
    const tr = el('tr', { class: 'admin-audit-row' });
    tr.appendChild(el('td', { class: 'admin-users-at' }, [formatAt(e.at)]));
    tr.appendChild(el('td', {}, [el('code', { class: 'admin-audit-action' }, [e.action])]));
    tr.appendChild(el('td', { class: 'admin-audit-admin' }, [e.admin_user_id]));
    tr.appendChild(el('td', {}, [summariseTarget(e)]));
    const details = e.metadata ? JSON.stringify(e.metadata) : '—';
    tr.appendChild(el('td', { class: 'admin-audit-details' }, [details]));
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  tableEl.appendChild(table);
}

export function viewAdminAudit<Model, Msg>(
  _state: Signal<Model>,
  _send: Send<Msg>,
): Mountable {
  const mountHook = onAdminMount('[data-admin-audit-root]', (root) => {
    let cancelled = false;

    void (async (): Promise<void> => {
      const res = await listAudit();
      if (cancelled) return;
      if (maybeRenderOverlay(root, res)) return;
      const statusEl = root.querySelector('[data-audit-status]');
      if (!res.ok) {
        if (statusEl) statusEl.textContent = t('admin.audit.error.generic');
        return;
      }
      if (statusEl) statusEl.textContent = '';
      const tableEl = root.querySelector('[data-audit-table]');
      if (tableEl) renderTable(tableEl, res.value.entries);
    })();

    return () => {
      cancelled = true;
    };
  });

  return main(
    {
      role: 'main',
      'aria-labelledby': 'admin-audit-heading',
      'data-admin-audit-root': '',
      'data-testid': 'admin-audit-view',
    },
    [
      mountHook,
      pageHeader({
        title: text(t('admin.audit.title')),
        titleId: 'admin-audit-heading',
        intro: text(t('admin.audit.intro')),
      }),
      div(
        {
          'data-audit-status': '',
          role: 'status',
          'aria-live': 'polite',
          class: 'admin-panel-status',
        },
        [text(t('admin.audit.loading'))],
      ),
      div({ 'data-audit-table': '', class: 'admin-audit-table-wrap' }, []),
    ],
  );
}
